import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, signal } from '@angular/core';
import { ResolvedTrackingLaneNode, TrackingLaneNodeStatus } from '../../../model/iTrackingLane';
import { YomaDateFormatPipe } from '../../../pipes/yoma-date-format.pipe';

interface Point {
  x: number;
  y: number;
}

function distance(a: Point, b: Point): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

function quadraticLen(p0: Point, p1: Point, p2: Point): number {
  return (distance(p0, p2) + distance(p0, p1) + distance(p1, p2)) / 2;
}

interface ChildDot {
  angleDeg: number;
  node: ResolvedTrackingLaneNode;
  revealDelayMs: number;
}

interface ChildDetour {
  nodeId: string;
  pathD: string;
  pathLen: number;
  fillLen: number;
  delayMs: number;
  dots: ChildDot[];
}

@Component({
  selector: 'tracking-lane-wheel',
  standalone: true,
  imports: [CommonModule, YomaDateFormatPipe],
  templateUrl: './tracking-lane-wheel.component.html',
  styleUrl: './tracking-lane-wheel.component.scss'
})
export class TrackingLaneWheelComponent implements OnChanges {
  private readonly ringThickness = 14;
  private readonly childArcInset = 20;
  private readonly childArcThickness = 8;
  private readonly ringDelayMs = 500;
  private readonly ringDurationMs = 1000;
  private readonly arcFillDurationMs = 800;
  private readonly connectorSweepDeg = 5;
  private readonly closingGapDeg = 8;

  @Input({ required: true }) nodes!: ResolvedTrackingLaneNode[];
  @Input() size = 320;
  @Input() cancelled = false;

  hoveredNode = signal<ResolvedTrackingLaneNode | null>(null);
  displayProgressPercent = 0;
  displayPlannedProgressPercent = 0;
  private displayFillLenByNodeId = new Map<string, number>();
  private hasRenderedOnce = false;

  ngOnChanges(): void {
    const targetProgress = this.progressPercent();
    const targetPlannedProgress = this.plannedProgressPercent();
    const detours = this.childDetours();

    if (!this.hasRenderedOnce) {
      this.hasRenderedOnce = true;
      this.displayProgressPercent = 0;
      this.displayPlannedProgressPercent = 0;
      detours.forEach(detour => this.displayFillLenByNodeId.set(detour.nodeId, 0));
      setTimeout(() => {
        this.displayProgressPercent = targetProgress;
        this.displayPlannedProgressPercent = targetPlannedProgress;
        detours.forEach(detour => this.displayFillLenByNodeId.set(detour.nodeId, detour.fillLen));
      });
      return;
    }

    this.displayProgressPercent = targetProgress;
    this.displayPlannedProgressPercent = targetPlannedProgress;
    detours.forEach(detour => this.displayFillLenByNodeId.set(detour.nodeId, detour.fillLen));
  }

  displayFillLen(nodeId: string): number {
    return this.displayFillLenByNodeId.get(nodeId) ?? 0;
  }

  progressPercent(): number {
    const list = this.nodes;
    const total = list.length;
    if (!total) return 0;

    const doneCount = list.filter(node => node.status === 'done').length;
    if (doneCount === total) return 100;

    const currentIndex = list.findIndex(node => node.status === 'current');
    const boundaryIndex = currentIndex >= 0 ? currentIndex : doneCount;
    return ((this.angleFor(boundaryIndex) + 90) / 360) * 100;
  }

  plannedProgressPercent(): number {
    const list = this.nodes;
    const total = list.length;
    if (!total) return 0;

    const doneCount = list.filter(node => node.status === 'done').length;
    if (doneCount === total) return 100;

    const currentIndex = list.findIndex(node => node.status === 'current');
    const boundaryIndex = currentIndex >= 0 ? currentIndex : doneCount;

    let cutIndex = boundaryIndex;
    while (cutIndex + 1 < total && list[cutIndex + 1].toBeDate) {
      cutIndex++;
    }
    if (cutIndex === boundaryIndex) return this.progressPercent();

    return ((this.angleFor(cutIndex) + 90) / 360) * 100;
  }

  currentNode(): ResolvedTrackingLaneNode | null {
    return this.nodes.find(node => node.status === 'current') ?? null;
  }

  isPlanned(node: ResolvedTrackingLaneNode): boolean {
    return !node.asIsDate && !!node.toBeDate;
  }

  isCurrentPlanned(): boolean {
    const current = this.currentNode();
    return !!current && this.isPlanned(current);
  }

  displayNode(): ResolvedTrackingLaneNode | null {
    return this.hoveredNode() ?? this.currentNode();
  }

  childDetours(): ChildDetour[] {
    const list = this.nodes;
    const total = list.length || 1;
    const sweepDeg = total > 1 ? this.angleFor(1) - this.angleFor(0) : 360;
    const size = this.size;
    const half = size / 2;
    const childRadius = half - this.childArcInset - this.childArcThickness / 2;
    const stationRadius = half - this.ringThickness / 2;
    const curveSweep = Math.min(this.connectorSweepDeg, sweepDeg / 3);
    const arcLen = childRadius * (((sweepDeg - curveSweep * 2) * Math.PI) / 180);
    const refDot = this.pointOnCircle(stationRadius, 0, half);
    const refCorner = this.pointOnCircle(childRadius, 0, half);
    const refArcEntry = this.pointOnCircle(childRadius, curveSweep, half);
    const connectorLen = quadraticLen(refDot, refCorner, refArcEntry);
    const pathLen = connectorLen * 2 + arcLen;

    const topDoneCount = list.filter(node => node.status === 'done').length;
    const ringReachDelayMs = (atIndex: number) =>
      topDoneCount > 0
        ? this.ringDelayMs + (atIndex / topDoneCount) * this.ringDurationMs
        : this.ringDelayMs + this.ringDurationMs;

    return list.map((node, index) => ({ node, index }))
      .filter(({ node }) => node.children.length > 0)
      .map(({ node, index }) => {
        const kids = node.children;
        const angleStart = this.angleFor(index);
        const angleEnd = angleStart + sweepDeg;
        const doneCount = kids.filter(child => child.status === 'done').length;
        const delayMs = ringReachDelayMs(index);
        const currentIndex = kids.findIndex(child => child.status === 'current');
        const fillFraction =
          currentIndex >= 0 ? (currentIndex + 1) / (kids.length + 1) : doneCount / kids.length;

        const dotStart = this.pointOnCircle(stationRadius, angleStart, half);
        const dotEnd = this.pointOnCircle(stationRadius, angleEnd, half);
        const cornerStart = this.pointOnCircle(childRadius, angleStart, half);
        const cornerEnd = this.pointOnCircle(childRadius, angleEnd, half);
        const arcEntry = this.pointOnCircle(childRadius, angleStart + curveSweep, half);
        const arcExit = this.pointOnCircle(childRadius, angleEnd - curveSweep, half);

        const pathD =
          `M ${dotStart.x} ${dotStart.y} ` +
          `Q ${cornerStart.x} ${cornerStart.y} ${arcEntry.x} ${arcEntry.y} ` +
          `A ${childRadius} ${childRadius} 0 0 1 ${arcExit.x} ${arcExit.y} ` +
          `Q ${cornerEnd.x} ${cornerEnd.y} ${dotEnd.x} ${dotEnd.y}`;

        const fillLen =
          currentIndex >= 0 ? connectorLen + fillFraction * arcLen : pathLen * fillFraction;

        return {
          nodeId: node.id,
          pathD,
          pathLen,
          fillLen,
          delayMs,
          dots: kids.map((child, i) => {
            const dotFraction = (i + 1) / (kids.length + 1);
            const sweepFraction = fillFraction > 0 ? fillFraction : 1;
            const revealDelayMs = delayMs + this.arcFillDurationMs * (dotFraction / sweepFraction);
            return {
              angleDeg: angleStart + sweepDeg * dotFraction,
              node: child,
              revealDelayMs
            };
          })
        };
      });
  }

  angleFor(index: number): number {
    const total = this.nodes.length;
    if (total <= 1) return -90;
    const usedSpan = 360 - this.closingGapDeg;
    return -90 + (usedSpan * index) / (total - 1);
  }

  private pointOnCircle(radius: number, angleDeg: number, center: number): Point {
    const rad = (angleDeg * Math.PI) / 180;
    return { x: center + radius * Math.cos(rad), y: center + radius * Math.sin(rad) };
  }

  statusLabel(status: TrackingLaneNodeStatus): string {
    switch (status) {
      case 'done':
        return 'Abgeschlossen';
      case 'current':
        return 'Aktueller Status';
      case 'upcoming':
        return 'Geplant';
    }
  }
}
